import { InputContainer } from './inputField.styles';
import {
  forwardRef,
  useState,
  useEffect,
  ChangeEvent,
  RefObject,
  ComponentPropsWithoutRef
} from 'react';
import { InputProps } from './inputField.interface';
import { DialCodeSelect } from './DialCodeSelect';
import { ErrorMessage } from './ErrorMessage';

export const InputField = forwardRef<HTMLInputElement | HTMLSelectElement, InputProps>(
  (props, ref) => {
    const {
      value,
      onChangeInternalValue,
      id,
      label,
      regex,
      isDropdown = false,
      errorMessages = [],
      errorRegexMessage = 'El valor no es válido',
      autoUpperCase = false,
      isDialCode = false,
      errortype = 'error',
      name,
      onChangeError,
      wrapLabel = false,
      restrictRegex = false,
      ...restProps
    } = props;
    const [internalValue, setInternalValue] = useState(value ?? '');
    const [errorRegex, setErrorRegex] = useState(false);
    const [dialCode, setDialCode] = useState('+52');

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    useEffect(() => {
      setInternalValue(value ?? '');
    }, []);

    useEffect(() => {
      isInvalidInput(internalValue);

      onChangeInternalValue &&
        internalValue &&
        onChangeInternalValue((isDialCode ? dialCode + ' ' : '') + internalValue, { name: name });
    }, [internalValue]);

    useEffect(() => {
      onChangeError && onChangeError(errorRegex);
    }, [errorRegex]);

    const isInvalidInput = (value: string | number | readonly string[]) => {
      if (regex && value !== '') {
        const isValid = regex.test(value.toString());
        if (!isValid) return setErrorRegex(true);
      }

      return setErrorRegex(false);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (autoUpperCase) return setInternalValue(e.target.value.toUpperCase());

      if (isDialCode) {
        const onlyNumsAndParentheses = e.target.value.replace(/[^0-9() \-+]/g, '');
        return setInternalValue(onlyNumsAndParentheses);
      }

      if (restrictRegex && regex && !regex.test(e.target.value) && e.target.value !== '') {
        return;
      }

      setInternalValue(e.target.value);
    };

    const handleChangeDropdown = (e: ChangeEvent<HTMLSelectElement>) => {
      setInternalValue(e.target.value);
    };

    const handleChangeDialCode = (e: ChangeEvent<HTMLSelectElement>) => {
      setDialCode(e.target.value);
    };

    const renderInput = () => {
      if (isDropdown) {
        return (
          <select
            className={errorMessages.length > 0 || errorRegex ? 'is-error' : ''}
            ref={ref as RefObject<HTMLSelectElement>}
            value={internalValue}
            onChange={handleChangeDropdown}
            name={name}
            id={inputId}
            {...(restProps as ComponentPropsWithoutRef<'select'>)}
          >
            <option value="" disabled hidden>
              {restProps.placeholder}
            </option>
            {props.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return (
        <input
          className={errorMessages.length > 0 || errorRegex ? 'is-error' : ''}
          ref={ref as RefObject<HTMLInputElement>}
          value={internalValue}
          onChange={handleChange}
          maxLength={isDialCode ? 15 : undefined}
          type={isDialCode ? 'tel' : undefined}
          name={name}
          id={inputId}
          {...(restProps as ComponentPropsWithoutRef<'input'>)}
        />
      );
    };

    return (
      <InputContainer wrapLabel={wrapLabel} isDialCode={isDialCode} errortype={errortype}>
        <label htmlFor={inputId}>{label}</label>

        <div className="input-wrapper">
          {isDialCode && <DialCodeSelect onChange={handleChangeDialCode} value={dialCode} />}

          {renderInput()}
        </div>
        {(errorRegex || errorMessages.length > 0) && (
          <ErrorMessage
            errorType={errortype}
            errorMessages={errorMessages}
            errorRegex={errorRegex}
            errorRegexMessage={errorRegexMessage}
          />
        )}
      </InputContainer>
    );
  }
);

InputField.displayName = 'InputField';
